import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, SetStateAction, useState} from 'react';

import {assert} from 'Asserts';
import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import {createProcedureRunPlans, CreateProcedureRunPlansParams} from 'procedures/network/procedureRunPlanDAO';
import EntityStore from 'stores/EntityStore';
import {useExperimentProcedure} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import ProcedureRunDescriptionEditor from 'studies/typed/shared/procedure-runs/design-tabs/ProcedureRunDescriptionEditor';
import MultiBatchSelector from 'studies/typed/shared/procedure-runs/multi-batch-selector/MultiBatchSelector';
import {LabeledProcedureDropdown} from 'studies/typed/shared/procedure-runs/procedure-selection/LabeledProcedureDropdown';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {navigateToStudyOverviewTab} from '../StudyNavigationUtils';
import {refreshStudyOverview} from './TrayTaskUtils';

export interface AddProcedureRunPlanModalProps {
  onRequestClose: () => void;
  studyType: StudyType;
  studyId: string;
  initialProcedureId?: string;
}

function getButtons(submitDisabled: boolean): Array<ModalButton> {
  return [
    {cancel: true, disabled: false, text: 'Cancel'},
    {
      disabled: submitDisabled,
      submit: true,
      text: 'Plan',
      style: ButtonStyle.SECONDARY,
      dataTestAttrs: {component: 'AddProcedureRunPlanModal', element: 'submitButton'},
    },
  ];
}

function AddProcedureRunPlanModalContent(props: {
  procedureId: string | null;
  setProcedureId: Dispatch<SetStateAction<string | null>>;
  description: string;
  setDescription: Dispatch<SetStateAction<string>>;
  dataEntryMode: ProcedureRunDataEntryMode;
  setDataEntryMode: Dispatch<SetStateAction<ProcedureRunDataEntryMode>>;
  procedureType: ProcedureType;
  onRequestClose: () => void;
}): JSX.Element {
  return (
    <>
      <LabeledProcedureDropdown
        procedureId={props.procedureId}
        setProcedureId={props.setProcedureId}
        onRequestCloseModal={props.onRequestClose}
        procedureType={props.procedureType}
        requiredField={true}
        dropdownDisabled={false}
        excludeLegacyProcedures={true}
        // We need the latest possible procedure version id because that gets stamped into the new run
        procedureFetchPolicy="network-only"
      />
      <ProcedureRunDescriptionEditor
        setDescription={props.setDescription}
        description={props.description}
        isBeingCreated={true}
      />
      {props.procedureType === ProcedureType.RECIPE && (
        <MultiBatchSelector
          dataEntryMode={props.dataEntryMode}
          onSelectDataEntryMode={props.setDataEntryMode}
          isDisabled={false}
        />
      )}
    </>
  );
}

function isSubmitDisabled(procedureId: string | null, procedureLoaded: boolean): boolean {
  return procedureId == null || !procedureLoaded;
}

export default function AddProcedureRunPlanModal(props: AddProcedureRunPlanModalProps): JSX.Element {
  const procedureType = getProcedureTypeForStudyType(props.studyType);
  const [procedureId, setProcedureId] = useState<string | null>(props.initialProcedureId ?? null);
  const [description, setDescription] = useState<string>('');
  const adapter = getTypedStudyAdapter(props.studyType);
  // PD studies have parallel execution pre-selected, while AD studies never use parallel execution.
  const defaultProcedureRunDataEntryMode = adapter.defaultProcedureRunDataEntryMode;
  assert(
    defaultProcedureRunDataEntryMode != null,
    `${props.studyType} studies do not support procedure run plans`
  );
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    defaultProcedureRunDataEntryMode
  );
  const {procedure} = useExperimentProcedure(procedureId ? {procedureId} : null, props.studyType);

  const currentUser = EntityStore.currentUser();
  assert(currentUser != null, 'Expected current user to be defined');

  const handleSubmit = async (): Promise<void> => {
    assert(procedure != null, 'Expected procedure to be defined');
    assert(
      procedure.workflowFlowchartConfigVersionId != null,
      'Expected workflowFlowchartConfigVersionId to be defined'
    );
    const createParams: CreateProcedureRunPlansParams = {
      description,
      dataEntryMode,
      creatorId: currentUser.id,
      workflowFlowchartConfigVersionId: procedure.workflowFlowchartConfigVersionId,
    };

    try {
      const response = await withLoadingScreen(
        createProcedureRunPlans(props.studyId, [createParams]),
        `Creating ${getProcedureRunUIStringForStudyType(props.studyType)} plan...`
      );

      // Refetch the listing table to show the new plan
      refreshStudyOverview(props.studyId, props.studyType);

      notifications.success(
        `${getProcedureRunUIStringForStudyType(props.studyType, {capital: true})} plan created successfully`
      );

      props.onRequestClose();

      // Navigate to the newly created plan
      const createdPlanId = response.createdIds[0];
      if (createdPlanId) {
        await navigateToStudyOverviewTab(props.studyId, createdPlanId);
      }
    } catch (e) {
      if (net.isServerRequestError(e)) {
        notifications.error(
          <ToastMessage
            header={`Failed to create ${getProcedureRunUIStringForStudyType(props.studyType)} plan`}
            body={notifications.getFriendlyErrorAsElement(e, '')}
          />
        );
      } else {
        throw e;
      }
    }
  };

  const submitDisabled = isSubmitDisabled(procedureId, procedure != null);

  return (
    <Modal
      title={
        <div className={css(styles.titleWrapper)}>
          <span>Plan {getProcedureRunUIStringForStudyType(props.studyType)}</span>
        </div>
      }
      onRequestClose={() => {
        props.onRequestClose();
      }}
      onRequestSubmit={handleSubmit}
      buttons={getButtons(submitDisabled)}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
      mediumLarge={true}
    >
      <AddProcedureRunPlanModalContent
        procedureId={procedureId}
        setProcedureId={setProcedureId}
        description={description ?? ''}
        setDescription={setDescription}
        dataEntryMode={dataEntryMode}
        setDataEntryMode={setDataEntryMode}
        procedureType={procedureType}
        onRequestClose={props.onRequestClose}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  titleWrapper: {display: 'flex', flexWrap: 'wrap', columnGap: '50px', rowGap: 10},
  errorText: {color: Colors.RED, marginTop: -12},
});
